import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from '../core/services/auth/auth.service';
import { ApiEndpointsService } from '../core/services/api-endpoints.service';
import { UserService } from '../core/services/user.service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { catchError } from 'rxjs/internal/operators/catchError';
import { throwError } from 'rxjs/internal/observable/throwError';
import { TranslateService } from '@ngx-translate/core';
import { AceFundDirectoryItem } from '../core/models/ace-fund-directory.model';
import { normalizeLanguage } from '../utils/language.util';

import { firstValueFrom, of, Subscription } from 'rxjs';

const ACE_ONLY_FUND_PRIVILEGES = [
  'viewDashboard',
  'viewManagement',
  'viewOnboarding',
  'viewChats',
  'viewFaq',
  'viewTeam',
  'viewChat',
];

interface ConfigVehicle {
  id: string;
  name?: string | null;
  code?: string | null;
  tenantId?: string | null;
  aceFundId?: string;
  investorNavigatorVehicleId?: string;
  privileges?: string[];
}

interface ConfigGp {
  id?: string;
  tenantId?: string;
  vehicles?: ConfigVehicle[];
}

interface AceFundDirectoryPage {
  items?: AceFundDirectoryItem[];
  nextCursor?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigService implements OnDestroy {
  response: any;
  private aceFundDirectory: AceFundDirectoryItem[] = [];
  private aceFundDirectoryLoaded = false;
  private readonly authSessionSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private apiEndpointsService: ApiEndpointsService,
    private userService: UserService,
    public translate: TranslateService,
    private authService: AuthService
  ) {
    this.authSessionSubscription = this.authService.sessionChanges$.subscribe(
      () => this.clearSessionCaches()
    );
  }

  ngOnDestroy(): void {
    this.authSessionSubscription.unsubscribe();
  }

  sessionuser: any;
  baseData: any;
  public notify = new BehaviorSubject<any>('');
  private baseDataSubject = new BehaviorSubject<any>(null);
  notifyObservable$ = this.notify.asObservable();
  baseDataObservable$ = this.baseDataSubject.asObservable();

  public async setBaseData(data: any) {
    if (data) {
      this.baseDataSubject.next(data);
    }
  }

  private clearSessionCaches(): void {
    this.clearTenantMappings();
    this.response = undefined;
    this.sessionuser = undefined;
    this.baseData = undefined;
    this.aceFundDirectory = [];
    this.aceFundDirectoryLoaded = false;
    this.baseDataSubject.next(null);
  }

  private clearTenantMappings(): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      const keysToRemove: string[] = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (
          key === 'selectedTenantId' ||
          key === 'activeTenantId' ||
          key?.startsWith('gp_tenant_map_') ||
          key?.startsWith('vehicle_tenant_map_')
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch {
      return;
    }
  }

  getAceFundDirectory(): readonly AceFundDirectoryItem[] {
    return this.aceFundDirectory;
  }

  hasAceFundDirectory(): boolean {
    return this.aceFundDirectoryLoaded;
  }

  public async notifyOther(data: any) {
    if (data) {
      await this.fetchuserData();
      this.notify.next(data);
    }
  }

  async init(): Promise<void> {
    // const pathname = window.location.pathname;
    try {
      let isRefresh = true;
      if (!this.authService.isAuthenticated()) {
        isRefresh = false;
      }
      await this.authService.authenticate();
      await this.fetchuserData(isRefresh);
      await this.fetchbaseData();
    } catch (error: any) {
      if (error.statusCode == 403) {
        this.sessionuser = {
          error: 'ACCESS_DENIED',
        };
      }
    }
  }

  async fetchuserData(isRefresh = false) {
    const sessionVersion = this.authService.getSessionVersion();
    return new Promise((resolve, _reject) => {
      this.http
        .get(this.apiEndpointsService.getSessionUser(isRefresh))
        .pipe(
          catchError((err: HttpErrorResponse) => {
            return throwError(() => err.error || 'Server error');
          })
        )
        .subscribe(
          data => {
            if (this.authService.getSessionVersion() !== sessionVersion) {
              resolve(true);
              return;
            }
            this.sessionuser = JSON.parse(JSON.stringify(data));
            const userLanguage = normalizeLanguage(
              this.sessionuser?.data?.state?.lang || this.userService.getStoredLanguage()
            );
            this.translate.use(userLanguage);
            this.userService.setUserConfig(
              `${this.sessionuser.data.firstName} ${this.sessionuser.data.lastName}`,
              userLanguage,
              this.sessionuser.data.id
            );
            this.userService.setUserData(this.sessionuser.data);
            resolve(true);
          },
          error => {
            if (this.authService.getSessionVersion() !== sessionVersion) {
              resolve(true);
              return;
            }
            if (error.statusCode == 403) {
              this.sessionuser = {
                error: 'ACCESS_DENIED',
              };
            }
            this.translate.use(this.userService.getStoredLanguage());
            resolve(true);
          }
        );
    });
  }

  async fetchbaseData() {
    const sessionVersion = this.authService.getSessionVersion();
    return new Promise((resolve, _reject) => {
      this.http
        .get(this.apiEndpointsService.getBaseData())
        .pipe(
          catchError((err: HttpErrorResponse) => {
            return throwError(() => err.error || 'Server error');
          })
        )
        .subscribe(
          async data => {
            if (this.authService.getSessionVersion() !== sessionVersion) {
              resolve(true);
              return;
            }
            this.baseData = JSON.parse(JSON.stringify(data));
            await this.mergeAceFunds();
            if (this.authService.getSessionVersion() !== sessionVersion) {
              resolve(true);
              return;
            }
            this.setBaseData(this.baseData.data);
            if (this.baseData.data.gps.length == 0) {
              this.sessionuser = {
                error: 'ACCESS_DENIED',
              };
            }
            resolve(true);
          },
          error => {
            if (this.authService.getSessionVersion() !== sessionVersion) {
              resolve(true);
              return;
            }
            if (error.statusCode == 403) {
              this.baseData = {
                error: 'ACCESS_DENIED',
              };
            }
            resolve(true);
          }
        );
    });
  }

  private async mergeAceFunds(): Promise<void> {
    const sessionVersion = this.authService.getSessionVersion();
    try {
      const aceFundsUrl = this.apiEndpointsService.getFunds();
      const items: AceFundDirectoryItem[] = [];
      let cursor: string | undefined;
      do {
        if (this.authService.getSessionVersion() !== sessionVersion) {
          return;
        }
        const pageUrl = cursor
          ? this.apiEndpointsService.getFunds(cursor)
          : aceFundsUrl;
        const response = await firstValueFrom(
          this.http.get(pageUrl).pipe(catchError(() => of(null)))
        );
        const envelope = response as { data?: AceFundDirectoryPage } | null;
        const page =
          envelope?.data ?? (response as AceFundDirectoryPage | null);
        if (!page) {
          throw new Error('ACE_FUND_DIRECTORY_UNAVAILABLE');
        }
        if (page?.items) {
          items.push(...page.items);
        }
        cursor = page?.nextCursor || undefined;
      } while (cursor);
      if (this.authService.getSessionVersion() !== sessionVersion) {
        return;
      }
      this.aceFundDirectory = items;
      this.aceFundDirectoryLoaded = true;
      const gps = (this.baseData?.data?.gps ||
        this.baseData?.gps ||
        []) as ConfigGp[];
      const fundsByVehicleId = new Map<string, AceFundDirectoryItem | null>();
      for (const fund of items) {
        if (!fund.vehicleId) continue;
        const existing = fundsByVehicleId.get(fund.vehicleId);
        if (existing === undefined) {
          fundsByVehicleId.set(fund.vehicleId, fund);
        } else if (existing && existing.id !== fund.id) {
          fundsByVehicleId.set(fund.vehicleId, null);
        }
      }

      for (const gp of gps) {
        const matchedTenants = new Set<string>();
        for (const vehicle of gp.vehicles || []) {
          const fund = fundsByVehicleId.get(vehicle.id);
          if (!fund) continue;
          vehicle.aceFundId = fund.id;
          vehicle.tenantId = fund.tenantId ?? vehicle.tenantId;
          if (fund.tenantId) matchedTenants.add(fund.tenantId);
          try {
            if (typeof localStorage !== 'undefined' && fund.tenantId) {
              localStorage.setItem(
                `vehicle_tenant_map_${vehicle.id}`,
                fund.tenantId
              );
              localStorage.setItem(
                `vehicle_tenant_map_${fund.id}`,
                fund.tenantId
              );
            }
          } catch {
            // Ignore
          }
        }
        if (matchedTenants.size === 1) {
          const [tenantId] = Array.from(matchedTenants);
          gp.tenantId = tenantId;
          try {
            if (typeof localStorage !== 'undefined' && gp.id) {
              localStorage.setItem(`gp_tenant_map_${gp.id}`, tenantId);
            }
          } catch {
            // Ignore
          }
        }
      }

      const gpCountByTenant = new Map<string, number>();
      for (const gp of gps) {
        if (gp.tenantId) {
          gpCountByTenant.set(
            gp.tenantId,
            (gpCountByTenant.get(gp.tenantId) ?? 0) + 1
          );
        }
      }

      for (const gp of gps) {
        const tenantId = gp.tenantId;
        if (tenantId && gpCountByTenant.get(tenantId) === 1) {
          const existingIds = new Set(
            (gp.vehicles || []).flatMap(vehicle =>
              [vehicle.id, vehicle.aceFundId].filter(Boolean)
            )
          );
          for (const fund of items) {
            if (fund.tenantId !== tenantId || fund.vehicleId || !fund.id) {
              continue;
            }
            if (existingIds.has(fund.id)) {
              continue;
            }
            gp.vehicles = gp.vehicles || [];
            gp.vehicles.push({
              id: fund.id,
              name: fund.name || fund.fundName || fund.code,
              code: fund.code,
              tenantId,
              aceFundId: fund.id,
              privileges: [...ACE_ONLY_FUND_PRIVILEGES],
            });
            existingIds.add(fund.id);
          }
        }
      }
    } catch {
      if (this.authService.getSessionVersion() !== sessionVersion) {
        return;
      }
      this.aceFundDirectoryLoaded = false;
      // Ignore if ACE backend call fails
    }
  }
}
